import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useConnectionStatus } from './useConnectionStatus';

export const useRealtimeSubscription = (table, filter = null, onUpdate = null) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isConnected } = useConnectionStatus();

  useEffect(() => {
    let subscription;
    let retryTimeout;
    let retryCount = 0;
    const maxRetries = 5;
    const baseRetryDelay = 2000;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        let query = supabase?.from(table)?.select('*');
        
        if (filter) {
          Object.entries(filter)?.forEach(([key, value]) => {
            query = query?.eq(key, value);
          });
        }

        const { data: fetchedData, error: fetchError } = await query;

        if (fetchError) throw fetchError;

        setData(fetchedData || []);
        retryCount = 0; // Reset retry count on success
      } catch (err) {
        console.error(`Error fetching ${table}:`, err);
        setError(err?.message);

        // Retry logic for transient errors
        if (retryCount < maxRetries && isConnected) {
          const delay = baseRetryDelay * Math.pow(2, retryCount);
          retryCount++;
          
          retryTimeout = setTimeout(() => {
            fetchData();
          }, delay);
        }
      } finally {
        setLoading(false);
      }
    };

    const setupSubscription = () => {
      try {
        let channel = supabase?.channel(`${table}_changes`)?.on('postgres_changes', {
            event: '*',
            schema: 'public',
            table: table,
            filter: filter ? Object.entries(filter)?.map(([key, value]) => `${key}=eq.${value}`)?.join(',') : undefined
          }, (payload) => {
            console.log(`${table} change received:`, payload);

            if (payload?.eventType === 'INSERT') {
              setData(prev => [...prev, payload?.new]);
            } else if (payload?.eventType === 'UPDATE') {
              setData(prev => prev?.map(item => 
                item?.id === payload?.new?.id ? payload?.new : item
              ));
            } else if (payload?.eventType === 'DELETE') {
              setData(prev => prev?.filter(item => item?.id !== payload?.old?.id));
            }

            if (onUpdate) {
              onUpdate(payload);
            }
          })?.subscribe((status) => {
            if (status === 'SUBSCRIBED') {
              console.log(`Subscribed to ${table} changes`);
            } else if (status === 'CHANNEL_ERROR') {
              console.error(`Subscription error for ${table}`);
              setError('Real-time subscription failed. Retrying...');
              
              // Retry subscription
              if (retryCount < maxRetries) {
                const delay = baseRetryDelay * Math.pow(2, retryCount);
                retryCount++;
                
                retryTimeout = setTimeout(() => {
                  setupSubscription();
                }, delay);
              }
            } else if (status === 'TIMED_OUT') {
              console.error(`Subscription timeout for ${table}`);
              setError('Connection timed out. Retrying...');
              
              // Retry subscription
              if (retryCount < maxRetries) {
                const delay = baseRetryDelay * Math.pow(2, retryCount);
                retryCount++;
                
                retryTimeout = setTimeout(() => {
                  setupSubscription();
                }, delay);
              }
            }
          });

        subscription = channel;
      } catch (err) {
        console.error(`Error setting up subscription for ${table}:`, err);
        setError('Failed to setup real-time updates');
      }
    };

    // Only fetch and subscribe if connected
    if (isConnected) {
      fetchData();
      setupSubscription();
    } else {
      setError('No connection. Data may be outdated.');
      setLoading(false);
    }

    return () => {
      if (subscription) {
        supabase?.removeChannel(subscription);
      }
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, [table, filter, onUpdate, isConnected]);

  return { data, loading, error, isConnected };
};